import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { CircleHelp } from 'lucide-react';
import { Tooltip, TooltipContent, TooltipTrigger } from 'components/ui/tooltip';
import useDepositStats from 'hooks/useDepositStats';
import DashboardSection from './components/DashboardSection';
import DashboardPage from './components/DashboardPage';
import PaidOperationsSummary from './components/PaidOperationsSummary';
import NationalQuotaSummary from './components/NationalQuotaSummary';
import FinanceDepositsCard from './components/FinanceDepositsCard';
import useDashboardScope from './useDashboardScope';

import { statValue, readFinanceStats, readQuotaStats, readPaidOperations } from './stats';

const AgencyDashboard = () => {
  const { api, campaignYear, enabled, queryOptions, scope } = useDashboardScope();
  const coreQuery = useQuery({
    ...queryOptions,
    queryKey: ['trip/stats/default/', ...scope],
    queryFn: api.list('trip/stats/default/', { year: campaignYear }),
  });
  const quotaQuery = useQuery({
    ...queryOptions,
    queryKey: ['trip/stats/category/', ...scope],
    queryFn: api.list('trip/stats/category/', { year: campaignYear }),
  });
  const financeQuery = useDepositStats();
  const stats = coreQuery.data?.data?.data;
  const registered = statValue(stats, 'Inscrits de la société');
  const core = Number.isFinite(registered) ? { registered, soldes: statValue(stats, 'Soldés') ?? null } : null;
  const quota = readQuotaStats(quotaQuery.data?.data?.data);
  const paymentValues = {
    no_confirmed_payment: statValue(stats, 'Sans paiement confirmé'),
    deposit_paid: statValue(stats, 'Acompte payé'),
    soldes: statValue(stats, 'Soldés'),
  };
  const payments = Object.values(paymentValues).every(Number.isFinite) ? paymentValues : null;
  const operations = readPaidOperations(stats);
  const finance = readFinanceStats(financeQuery.data?.data?.data);
  const queries = [coreQuery, quotaQuery, financeQuery];
  const hasCompleteData = !!(core && payments && operations && quota && finance);
  const rows = payments
    ? [
        { label: 'Sans paiement', value: payments.no_confirmed_payment, color: 'bg-slate-300' },
        { label: 'Acompte payé', value: payments.deposit_paid, color: 'bg-emerald-300' },
        { label: 'Soldés', value: payments.soldes, color: 'bg-emerald-700' },
      ]
    : [];
  return (
    <DashboardPage
      ariaLabel="Tableau de bord de l'agence"
      title="Tableau de bord"
      subtitle={`Campagne ${campaignYear || 'indisponible'}`}
      enabled={enabled}
      queries={queries}
      hasCompleteData={hasCompleteData}
      contentClassName="grid items-start gap-4 lg:grid-cols-2"
    >
      <div className="contents lg:grid lg:min-w-0 lg:gap-4">
        <DashboardSection title="Situation de l'agence" query={coreQuery} available={!!core} className="order-1">
          {core && (
            <>
              <div className="grid grid-cols-2 items-center gap-5 py-2">
                <div>
                  <p className="text-[44px] font-semibold leading-tight tracking-[-1.5px] text-emerald-800">
                    {core.soldes == null ? '—' : core.soldes.toLocaleString('fr-FR')}
                  </p>
                  <p className="mt-1.5 text-sm text-slate-500">Pèlerins soldés</p>
                </div>
                <div className="border-l pl-5">
                  <p className="text-3xl font-semibold">{core.registered.toLocaleString('fr-FR')}</p>
                  <p className="mt-1.5 text-sm text-slate-500">Pèlerins inscrits</p>
                </div>
              </div>
              <div className="mt-3 border-t pt-4">
                <h3 className="mb-3.5 flex items-center gap-1 text-sm font-semibold">
                  Situation des paiements
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <button
                        type="button"
                        aria-label="À propos de la situation des paiements"
                        className="rounded-full text-slate-500 focus-visible:outline focus-visible:outline-2"
                      >
                        <CircleHelp className="h-4 w-4" />
                      </button>
                    </TooltipTrigger>
                    <TooltipContent>
                      Sans paiement, acompte payé et soldés composent l’ensemble des pèlerins inscrits.
                    </TooltipContent>
                  </Tooltip>
                </h3>
                {payments ? (
                  <>
                    <div className="mb-3 flex h-1.5 overflow-hidden rounded-sm bg-slate-100" aria-hidden="true">
                      {rows.map((row) => (
                        <div
                          key={row.label}
                          className={row.color}
                          style={{ width: `${core.registered ? (row.value / core.registered) * 100 : 0}%` }}
                        />
                      ))}
                    </div>
                    <dl className="grid grid-cols-3 gap-3">
                      {rows.map((row) => (
                        <div key={row.label}>
                          <dd className="text-2xl font-semibold">{row.value.toLocaleString('fr-FR')}</dd>
                          <dt className="mt-1 text-sm text-slate-500">{row.label}</dt>
                        </div>
                      ))}
                    </dl>
                  </>
                ) : (
                  <p role="alert" className="py-4 text-sm text-slate-600">
                    Situation des paiements indisponible : les données ne permettent pas une répartition complète des
                    inscrits.
                  </p>
                )}
              </div>
            </>
          )}
        </DashboardSection>
        <DashboardSection
          title="Suivi opérationnel"
          help="Aptitude et passeport suivis sur la base des pèlerins soldés."
          query={coreQuery}
          available={!!operations}
          className="order-3"
        >
          {operations && <PaidOperationsSummary operations={operations} baseLabel="pèlerins soldés" />}
        </DashboardSection>
        <DashboardSection title="Finance" query={financeQuery} available={!!finance} className="order-4">
          {finance && <FinanceDepositsCard confirmed={finance.confirmed} pending={finance.pending} />}
        </DashboardSection>
      </div>
      <DashboardSection
        title="Quota national"
        help="Quota commun à toutes les sociétés. Seuls les pèlerins soldés occupent une place."
        query={quotaQuery}
        available={!!quota}
        className="order-2 lg:order-none"
      >
        {quota && <NationalQuotaSummary quota={quota} />}
      </DashboardSection>
    </DashboardPage>
  );
};
export default AgencyDashboard;
